import * as React from "react"
import {Checkbox} from "../../../lib/sbhtml/checkbox";
import {SingleBox as Select} from "../../../lib/sbhtml/select";
import {range, toString} from "../../../lib/utils";
import {ArticleTemplate, Column, isColumn} from "../data";

export type ArticleTemplateListProps = ValueProps & CallbackProps

export type ValueProps = ToolbarProps & GridProps

type ToolbarProps = {
	canEdit: boolean
	canMulti: boolean
}

type GridProps = {
	articleTemplates: ArticleTemplate[]
	selected: string[]
	columns: Column
	priorities: {[K in Column]: number}
}

export type CallbackProps = ToolbarCallbackProps & GridCallbackProps

type ToolbarCallbackProps = {
	addArticle: () => void
	editSelectedArticle: () => void
	copyArticles: () => void
	deleteArticles: () => void
}

type GridCallbackProps = {
	editArticle: (id: string) => void
	selectAll: () => void
	unselectAll: () => void
	select: (id: string) => void
	unselect: (id: string) => void
	changeColumn: (id: string, column: Column) => void
	changePriority: (id: string, priority: number) => void
}

export const ArticleTemplateList = (props:ArticleTemplateListProps) => {
	//todo: replace with fixed data grid if possible
	return <div>
		<Toolbar
			canEdit={props.canEdit}
			canMulti={props.canMulti}
			addArticle={props.addArticle}
			editSelectedArticle={props.editSelectedArticle}
			copyArticles={props.copyArticles}
			deleteArticles={props.deleteArticles}

		/>
		<LayoutGrid
			articleTemplates={props.articleTemplates}
			selected={props.selected}
			columns={props.columns}
			priorities={props.priorities}
			editArticle={props.editArticle}
			selectAll={props.selectAll}
			unselectAll={props.unselectAll}
			select={props.select}
			unselect={props.unselect}
			changeColumn={props.changeColumn}
			changePriority={props.changePriority}
		/>
	</div>
}

const Toolbar = (props: ToolbarProps & ToolbarCallbackProps) => {
	return <div className="sbToolbar sbRoundedEdges">
		<ToolbarButton
			imgName={'addIcon.png'}
			disabled={false}
			onClick={props.addArticle}
		/>
		<ToolbarButton
			imgName={props.canEdit ? 'editIcon.png' : 'editIconDisabled.png'}
			disabled={!props.canEdit}
			onClick={props.editSelectedArticle}
		/>
		<ToolbarButton
			imgName={props.canMulti ? 'copy.png' : 'copyDisabled.png'}
			disabled={!props.canMulti}
			onClick={props.copyArticles}
		/>
		<ToolbarButton
			imgName={props.canMulti ? 'deleteIcon.png' : 'deleteIconDisabled.png'}
			disabled={!props.canMulti}
			onClick={props.deleteArticles}
		/>
	</div>
}

type ToolbarButtonProps = {imgName: string, disabled: boolean, onClick: () => void}
const ToolbarButton = (props: ToolbarButtonProps) => {
	const onClick = !props.disabled ? {onClick: props.onClick} : {}
	return <button
		{...onClick}
		disabled={props.disabled}
		className="sbToolbarButton"
		style={{backgroundImage: `url('/sb/static/images/icons/${props.imgName}')`}}
		type="button"
	/>
}

const LayoutGrid = (props: GridProps & GridCallbackProps) => {
	const imgStyle = {cursor: 'pointer', width: '16px', height: '16px'}
	return <div className="dataGridPanel sbRoundedEdges" style={{width: '100%'}}>
		<table className="dataGrid sbRoundedEdges" style={{width: '100%'}} cellSpacing="0" cellPadding="2">
			<thead>
			<tr className="dataGridTitlebar sbRoundedEdges" style={{height: '30px'}}>
				<th style={{width: '40px', textAlign: 'center'}}>
					<img
						src='/sb/static/images/icons/delete.png'
						title='check all boxes'
						style={imgStyle}
						onClick={props.unselectAll}
					/>
					<img
						src='/sb/static/images/icons/accept.png'
						title='uncheck all boxes'
						style={imgStyle}
						onClick={props.selectAll}
					/>
				</th>
				<th style={{width: '40px', textAlign: 'center'}}>#</th>
				<th style={{textAlign: 'left'}}>Title</th>
				<th style={{width: '80px', textAlign: 'center'}}>Column</th>
				<th style={{width: '80px', textAlign: 'center'}}>Priority</th>
			</tr>
			</thead>
			<tbody>
				{props.articleTemplates.map((a: ArticleTemplate, i) => {
					const checked = props.selected.find((id) => id === a.id) !== undefined
					return <ArticleTemplateRow
						key={`article-${a.id}`}
						{...a}
						rowClass={checked ? 'rowOn' : (i%2 ? 'row1' : 'row0')}
						checked={checked}
						editArticle={props.editArticle}
						onSelectChange={checked ? props.unselect : props.select}
						onColumnChange={props.changeColumn}
						onPriorityChange={props.changePriority}
						maxColumn={props.columns}
						maxPriority={props.priorities[a.column]}
					/>
				})}
			</tbody>
		</table>
	</div>
}

type RowClass = 'row0' | 'row1' | 'rowOn'
type ArticleTemplateProps = ArticleTemplate & {
	rowClass: RowClass
	checked: boolean
	editArticle: (id:string) => void
	onSelectChange: (id: string) => void
	onColumnChange: (id: string, column: Column) => void
	onPriorityChange: (id: string, priority: number) => void
	maxColumn: Column
	maxPriority: number
}
const ArticleTemplateRow = (props: ArticleTemplateProps) => {
	const editOnClick = () => props.editArticle(props.id)
	return <tr className={props.rowClass}>
		<td style={{textAlign: 'center', cursor: 'pointer'}}>
			<Checkbox checked={props.checked} onChange={() => props.onSelectChange(props.id)}/>
		</td>
		<td style={{textAlign: 'center', cursor: 'pointer'}} onClick={editOnClick}>{props.id}</td>
		<td style={{textAlign: 'left', cursor: 'pointer'}} onClick={editOnClick}>{props.title}</td>
		<td style={{textAlign: 'center', cursor: 'pointer'}}>
			<Select
				value={props.column}
				onChange={(c) => props.onColumnChange(props.id, isColumn(c) ? c : '1') }
				name={`c-${props.id}`}
			>
				{range(1,parseInt(props.maxColumn)).map((column) => {
					return <option key={`c-${props.id}-${column}`} value={toString(column)}>{column}</option>
				})}
			</Select>
		</td>
		<td style={{textAlign: 'center', cursor: 'pointer'}}>
			<Select
				value={toString(props.priority)}
				onChange={(p) => props.onPriorityChange(props.id, parseInt(p)) }
				name={`p-${props.id}`}
			>
				{range(1,props.maxPriority).map((priority) => {
					return <option key={`p-${props.id}-${priority}`} value={toString(priority)}>{priority}</option>
				})}
			</Select>
		</td>
	</tr>
}



// WEBPACK FOOTER //
// ./src/communications/article-templates/writable/presentation.tsx